import axios from "axios";
import type { Snippet } from "../snippets/SnippetContainer";
import type { FileRef } from "../WorkspaceContext";
import api from "../../../api";
import { completeTransferNotice, showTransferNotice, type TransferItemType } from "../../../shared/components/TransferNotifications/TransferNotifications";

const API = process.env.REACT_APP_API_URL;





type Folder = {
    _id: string;
    creatorId: string;
    fname: string;
    snippets?: string[];
    fileRefs?: string[];
    folders?: string[];
};


export async function downloadSnippet(name: string, contents: string, mime: string) {
    const noticeId = showTransferNotice({
        direction: "download",
        itemType: "snippet",
        name,
    });

    const blob = new Blob([contents], { type: mime });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url

    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
    completeTransferNotice(noticeId);

}

export async function downloadFile(roomId: string, fileId: string, displayName?: string) {
    try {
        const { data } = await api.get(`/rooms/${roomId}/files/${fileId}/getDownload`);

        if (!data?.ok || !data?.downloadUrl) {
            throw new Error(data?.message || "No download URL returned");
        }

        const fileName = displayName || data.fileName || "download";
        const noticeId = showTransferNotice({
            direction: "download",
            itemType: "file",
            name: fileName,
        });

        const fileRes = await fetch(data.downloadUrl);

        if (!fileRes.ok) {
            throw new Error("Failed to fetch file from S3");
        }

        const blob = await fileRes.blob();

        const url = URL.createObjectURL(
            new Blob([blob], {
                type: data.mime || blob.type || "application/octet-stream",
            })
        );

        const a = document.createElement("a");
        a.href = url;

        // this should be "poster.ai", "image.png", "document.pdf", etc.
        a.download = fileName;

        document.body.appendChild(a);
        a.click();
        a.remove();

        setTimeout(() => URL.revokeObjectURL(url), 1000);
        completeTransferNotice(noticeId);
    } catch (error) {
        throw error;
    }
}




export async function downloadMultiple(
    roomId: string,
    snippetIds?: string[],
    fileIds?: string[],
    folderIds?: string[],
    notice?: {
        name?: string;
        itemType?: TransferItemType;
    }
) {
    try {
        if (!roomId) return;
        let noticeId: number | null = null;
        const inferredItemType: TransferItemType = folderIds?.length ? "folder" : "file";

        if (notice?.name) {
            noticeId = showTransferNotice({
                direction: "download",
                itemType: notice.itemType || inferredItemType,
                name: notice.name,
            });
        }

        const res = await api.post(
            `/rooms/${roomId}/files/downloadzip`,
            { fileIds, snippetIds, folderIds }, // ✅ correct key
            { responseType: "blob" }
        );

        const disposition = res.headers["content-disposition"] as string | undefined;

        const raw =
            disposition?.match(/filename\*=(?:UTF-8'')?([^;]+)/i)?.[1] ??
            disposition?.match(/filename="?([^"]+)"?/i)?.[1] ??
            "download.zip";

        const filename = decodeURIComponent(String(raw)).trim().replace(/^"|"$/g, "");
        const zipName = filename.toLowerCase().endsWith(".zip") ? filename : `${filename}.zip`;

        if (!notice?.name) {
            noticeId = showTransferNotice({
                direction: "download",
                itemType: inferredItemType,
                name: zipName,
            });
        }

        const blob = new Blob([res.data], {
            type: res.headers["content-type"] || "application/zip",
        });

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = zipName;

        document.body.appendChild(a);
        a.click();
        a.remove();

        setTimeout(() => URL.revokeObjectURL(url), 0);
        if (noticeId) completeTransferNotice(noticeId);
    } catch (error) {
        throw error;
    }
}
